import { format, isValid, parseISO } from "date-fns";
import { Calendar, Activity } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { cn } from "@/lib/utils";
import type {
  ViewPeriod,
  DisplayCurrency,
} from "@/hooks/finance/useCashFlowStatement";

type Props = {
  dateFrom: string;
  dateTo: string;
  viewPeriod: ViewPeriod;
  displayCurrency: DisplayCurrency;

  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onViewPeriodChange: (value: ViewPeriod) => void;
  onDisplayCurrencyChange: (value: DisplayCurrency) => void;

  title?: string;
  subtitle?: string;
  badgeLabel?: string;
};

export function CashFlowStatementHeader({
  dateFrom,
  dateTo,
  viewPeriod,
  displayCurrency,
  onDateFromChange,
  onDateToChange,
  onViewPeriodChange,
  onDisplayCurrencyChange,
  title = "Cash Intelligence",
  subtitle,
  badgeLabel = "IAS 7 Pro",
}: Props) {
  const resolvedSubtitle =
    subtitle ??
    `Analysis for ${
      isValid(parseISO(dateFrom)) ? format(parseISO(dateFrom), "MMM d") : "—"
    } — ${
      isValid(parseISO(dateTo)) ? format(parseISO(dateTo), "MMM d, yyyy") : "—"
    }`;

  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <div className="h-8 w-1 bg-primary rounded-full" />
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            {title}
          </h1>
        </div>

        <div className="text-muted-foreground font-medium flex items-center gap-2">
          <Badge
            variant="secondary"
            className="bg-slate-100 hover:bg-slate-200 text-slate-600 border-none px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider"
          >
            {badgeLabel}
          </Badge>
          {resolvedSubtitle}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Card className="flex items-center gap-1.5 p-1 bg-slate-50 border rounded-lg shadow-sm">
          <div className="flex items-center gap-2 px-2 border-r pr-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 px-2 text-xs font-semibold gap-1.5 hover:bg-white"
                >
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {isValid(parseISO(dateFrom))
                    ? format(parseISO(dateFrom), "MMM d, yyyy")
                    : "Start date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={
                    isValid(parseISO(dateFrom)) ? parseISO(dateFrom) : undefined
                  }
                  onSelect={(date) =>
                    date && onDateFromChange(format(date, "yyyy-MM-dd"))
                  }
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>

            <span className="text-muted-foreground">—</span>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 px-2 text-xs font-semibold gap-1.5 hover:bg-white"
                >
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {isValid(parseISO(dateTo))
                    ? format(parseISO(dateTo), "MMM d, yyyy")
                    : "End date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={
                    isValid(parseISO(dateTo)) ? parseISO(dateTo) : undefined
                  }
                  onSelect={(date) =>
                    date && onDateToChange(format(date, "yyyy-MM-dd"))
                  }
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          <Select
            value={viewPeriod}
            onValueChange={(v) => onViewPeriodChange(v as ViewPeriod)}
          >
            <SelectTrigger className="w-28 h-8 border-none bg-transparent hover:bg-white shadow-none text-xs font-semibold">
              <Activity className="h-3.5 w-3.5 mr-2 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
            </SelectContent>
          </Select>

          <div className="h-5 w-px bg-slate-200 mx-1" />

          <div className="flex p-0.5 gap-0.5">
            {(["ETB", "USD"] as const).map((currency) => (
              <button
                key={currency}
                type="button"
                className={cn(
                  "px-3 py-1 rounded-md text-[10px] font-bold transition-all",
                  displayCurrency === currency
                    ? "bg-white text-primary shadow-sm ring-1 ring-slate-200"
                    : "text-slate-500 hover:text-slate-900"
                )}
                onClick={() => onDisplayCurrencyChange(currency)}
              >
                {currency}
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}